import { useCallback, useEffect, useMemo, useRef, type CSSProperties } from "react";
import { styled } from "@pigment-css/react";
import type { BarChart as BarChartSchema, YSeries } from "@acto/genui-components-schema";
import { baseTheme } from "@acto/web-common/theme";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  DefaultZIndexes,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ZIndexLayer,
  getNiceTickValues,
  useChartHeight,
  usePlotArea,
} from "recharts";
import { formatCompact, formatCurrency } from "@acto/taskpane/field-sales-2/utils/format";
import { CurrencyCode } from "@acto/taskpane/core/utils/currencyCode";

export const LEAD_SERIES_FILL = baseTheme.fieldSalesTwo.colors.charts.primary;
export const COMPARISON_SERIES_FILL = baseTheme.fieldSalesTwo.colors.charts.secondary;
const MUTED_FILL = baseTheme.fieldSalesTwo.colors.charts.muted;
const CHART_LINE_PRIMARY = baseTheme.fieldSalesTwo.colors.charts.linePrimary;
const CHART_LINE_SECONDARY = baseTheme.fieldSalesTwo.colors.charts.lineSecondary;
const CHART_HALO = baseTheme.fieldSalesTwo.colors.neutral[50];
const CHART_GRID = baseTheme.fieldSalesTwo.colors.neutral[200];
const CHART_AXIS = baseTheme.fieldSalesTwo.colors.neutral[800];
const CHART_VALUE = baseTheme.fieldSalesTwo.colors.neutral[900];
const CHART_AXIS_FONT_WEIGHT = baseTheme.fieldSalesTwo.font.weight["440"];

// A 12-month window spans 13 month buckets whenever it does not start on the 1st.
const PAIR_MODE_MAX_BARS = 13;

const DIMMED_OPACITY = 0.5;
const dimVar = (index: number) => `--trend-bar-dim-${index}`;
const FOCUSED_DOT_GROWTH = "1px";
const focusVar = (index: number) => `--trend-dot-focus-${index}`;

const MAX_BAR_WIDTH = 28;
const MARKER_WIDTH_RATIO = 0.2;
const MARKER_MIN_WIDTH = 2;
const BAR_GAP = 2;
const ZERO_TICK_HEIGHT = 2;
const TARGET_LABEL_COUNT = 7;
const AXIS_WIDTH = 36;
const AXIS_LABEL_INSET = 4;
const AXIS_LABEL_GAP = 8;
const AXIS_LABEL_FONT_FAMILY = "InterVariable, sans-serif";
const AXIS_LABEL_FONT = `${CHART_AXIS_FONT_WEIGHT} 11px ${AXIS_LABEL_FONT_FAMILY}`;

// recharts breaks a tick label at any whitespace once it outgrows the axis width, which strands the
// unit on a line of its own. It does not break at a no-break space.
const keepOnOneLine = (label: string) => label.replace(/ /g, "\u00a0");
// Matches recharts' default YAxis tickCount, so adding a line does not change the number of gridlines.
const Y_TICK_COUNT = 5;

const LINE_AXIS_ID = "line";
const LINE_STYLE = {
  lead: { stroke: CHART_LINE_PRIMARY, strokeWidth: 2, haloWidth: 5.5, dotRadius: 3.5 },
  comparison: { stroke: CHART_LINE_SECONDARY, strokeWidth: 1.6, haloWidth: 4, dotRadius: 2.5 },
} as const;

// The tap targets have to be the topmost layer, otherwise a tap lands on the bar or the tick label
// underneath them and never reaches the band.
const BAND_Z_INDEX = DefaultZIndexes.label + 1;

const ChartWrap = styled("div")({
  width: "100%",
  height: 240,
  userSelect: "none",
  WebkitUserSelect: "none",
  // The per-column dim opacity has to stay an inline custom property (CSS cannot express it), but its
  // transition belongs here so reduced motion can cancel it. Focus still applies, just instantly.
  "& [data-trend-dim]": {
    transition: "opacity 200ms ease",
  },
  "& [data-trend-dim] circle": {
    transition: "r 200ms ease",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& [data-trend-dim], & [data-trend-dim] circle": {
      transition: "none",
    },
  },
});

// Both shapes cap their own width, anchor to the inner edge of their half-slot, then
// shift inward by centerShift so the combined marker+gap+bar block is centred on the
// band tick (the x-axis dot/label) rather than seamed across it. maxBarSize is avoided
// because it centres each bar within its half-slot, pushing the pair apart on wide charts.
const pairGeometry = (slotWidth: number) => {
  const barWidth = Math.min(slotWidth, MAX_BAR_WIDTH);
  const markerWidth = Math.max(MARKER_MIN_WIDTH, barWidth * MARKER_WIDTH_RATIO);
  // Half the bar/marker width difference: moving both left by this re-centres the
  // block on the tick, since the bars are otherwise anchored either side of the seam.
  const centerShift = (barWidth - markerWidth) / 2;

  return { barWidth, markerWidth, centerShift };
};

// Draws a bar from the zero line to the value, or a thin baseline tick for exactly 0€ so it
// reads as a deliberate "no revenue this period", not missing data. Negative bars arrive
// with a negative height (y at the value, growing back up to the zero line), so normalise.
const drawBarRect = (x: number, width: number, y: number, height: number, rx: number, fill?: string) => {
  if (height === 0) {
    return <rect x={x} y={y - ZERO_TICK_HEIGHT / 2} width={width} height={ZERO_TICK_HEIGHT} rx={1} fill={fill} />;
  }

  return <rect x={x} y={Math.min(y, y + height)} width={width} height={Math.abs(height)} rx={rx} fill={fill} />;
};

interface BarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  /** Injected by recharts: the bar's data index. */
  index?: number;
  /** Injected by recharts: the bar's own data row, so a shape can read its sibling series. */
  payload?: Record<string, unknown>;
}

const dimProps = (index: number | undefined): { "data-trend-dim"?: boolean; style?: CSSProperties } =>
  index === undefined ? {} : { "data-trend-dim": true, style: { opacity: `var(${dimVar(index)}, 1)` } };

// The schema distinguishes a null cell, meaning the band carries no value for that series, from a
// measured 0. Only the former leaves the other half-slot empty: a 0 is a value, and the pair is
// what makes its zero tick readable as one.
const siblingMissing = (payload: BarShapeProps["payload"], field: string | undefined) => {
  const value = field === undefined ? undefined : payload?.[field];

  return value === null || value === undefined;
};

export type BarEmphasis = "highlighted" | "muted";
type BarEmphasisPerBar = readonly (BarEmphasis | undefined)[];

const LeadBarShape = (
  props: BarShapeProps & { pairMode?: boolean; comparisonField?: string; barEmphasis?: BarEmphasisPerBar },
) => {
  const { x = 0, y = 0, width = 0, height = 0, index, payload, pairMode = false, comparisonField, barEmphasis } = props;
  const { barWidth, centerShift } = pairGeometry(width);
  const fill = index !== undefined && barEmphasis?.[index] === "muted" ? MUTED_FILL : props.fill;

  if (!pairMode) {
    return <g {...dimProps(index)}>{drawBarRect(x + (width - barWidth) / 2, barWidth, y, height, 2, fill)}</g>;
  }

  const seamX = x - BAR_GAP / 2;
  const barX = siblingMissing(payload, comparisonField) ? seamX - barWidth / 2 : x - centerShift;

  return <g {...dimProps(index)}>{drawBarRect(barX, barWidth, y, height, 2, fill)}</g>;
};

// Marker width tracks the band slot (capped to match the lead bar), not the sibling
// bar's value, so empty months keep the same marker width as paired months.
const PastBarShape = (props: BarShapeProps & { leadField: string }) => {
  const { x = 0, y = 0, width = 0, height = 0, fill, index, payload, leadField } = props;
  const { barWidth, markerWidth, centerShift } = pairGeometry(width);
  const seamX = x + width + BAR_GAP / 2;

  if (siblingMissing(payload, leadField)) {
    return <g {...dimProps(index)}>{drawBarRect(seamX - barWidth / 2, barWidth, y, height, 2, fill)}</g>;
  }

  const markerX = x + width - markerWidth - centerShift;

  return <g {...dimProps(index)}>{drawBarRect(markerX, markerWidth, y, height, 1, fill)}</g>;
};

/**
 * Transparent tap target per category band, spanning the chart top to bottom so a near-zero bar and
 * its axis label are as easy to hit as a tall bar. Must render inside the chart, which is where the
 * geometry it positions against comes from.
 */
const BandTapTargets = ({ count, onSelect }: { count: number; onSelect: (index: number) => void }) => {
  const plot = usePlotArea();
  const chartHeight = useChartHeight();
  if (plot === undefined || chartHeight === undefined || count === 0) return null;

  const bandWidth = plot.width / count;

  return (
    <ZIndexLayer zIndex={BAND_Z_INDEX}>
      {Array.from({ length: count }, (_, index) => (
        <rect
          key={index}
          data-band-index={index}
          x={plot.x + bandWidth * index}
          y={0}
          width={bandWidth}
          height={chartHeight}
          // Important, no own pointer-events: an own value beats an inherited "none", escaping a
          // caller's inert state. The transparent fill already hit-tests as painted.
          fill="transparent"
          onClick={() => onSelect(index)}
        />
      ))}
    </ZIndexLayer>
  );
};

interface LineDotProps {
  cx?: number;
  cy?: number;
  index?: number;
  value?: number | null;
  radius: number;
  fill: string;
}

const LineDot = ({ cx, cy, index, value, radius, fill }: LineDotProps) => {
  if (cx === undefined || cy === undefined || value === null || value === undefined) return null;

  return (
    <g {...dimProps(index)}>
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        // CSS `r` wins over the attribute, so the focused column's dot grows without re-rendering the
        // chart; where CSS geometry is unsupported the attribute radius stays.
        style={
          index === undefined ? undefined : ({ r: `calc(${radius}px + var(${focusVar(index)}, 0px))` } as CSSProperties)
        }
        fill={fill}
        stroke={CHART_HALO}
        strokeWidth={1.5}
      />
    </g>
  );
};

const numericValues = (data: BarChartSchema["data"], fields: (string | undefined)[]): number[] =>
  data.flatMap((row) =>
    fields.flatMap((field) => {
      const value = field === undefined ? undefined : row[field];
      return typeof value === "number" && Number.isFinite(value) ? [value] : [];
    }),
  );

// The smallest step from 1, 2, 2.5 and 5 per order of magnitude that covers the rough step and is a
// whole multiple of `unit`. A line counting pieces has unit 1, so it never prints ticks like "2,5 St.".
const niceStepOf = (roughStep: number, unit: number): number => {
  if (roughStep <= unit) return unit;

  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const isWholeUnits = (value: number) => Math.abs(value / unit - Math.round(value / unit)) < 1e-9;
  const step = [1, 2, 2.5, 5, 10]
    .map((multiple) => multiple * magnitude)
    .find((candidate) => isWholeUnits(candidate) && candidate >= roughStep);

  return step ?? 10 * magnitude;
};

const niceBarTicks = (barValues: number[]): number[] =>
  getNiceTickValues([Math.min(0, ...barValues), Math.max(0, ...barValues)], Y_TICK_COUNT, true, "snap125");

let labelContext: CanvasRenderingContext2D | null | undefined;

const measureLabel = (label: string): number | undefined => {
  if (labelContext === undefined) {
    labelContext = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  }
  if (!labelContext) return undefined;
  labelContext.font = AXIS_LABEL_FONT;
  return labelContext.measureText(label).width;
};

// Sized to the widest label: that label then sits AXIS_LABEL_INSET from the card edge, and every label
// keeps AXIS_LABEL_GAP to the gridlines on either axis.
const axisWidthFor = (labels: string[]): number => {
  const widths = labels.map(measureLabel);
  if (widths.length === 0 || widths.some((width) => width === undefined)) return AXIS_WIDTH;
  return Math.ceil(AXIS_LABEL_INSET + Math.max(...(widths as number[])) + AXIS_LABEL_GAP);
};

/**
 * Ticks for both axes such that every gridline carries one value on each side: the line axis gets
 * the bar axis's tick count and its zero on the same gridline, with the step scaled to the line's
 * own maximum. Undefined when the bars have no positive range to scale against.
 */
export const alignedAxisTicks = (
  barValues: number[],
  lineValues: number[],
  lineTickUnit = 1,
): { barTicks: number[]; lineTicks: number[] } | undefined => {
  const barTicks = niceBarTicks(barValues);
  const barStep = barTicks.length > 1 ? barTicks[1] - barTicks[0] : 0;
  const top = barTicks[barTicks.length - 1] ?? 0;
  if (barStep <= 0 || top <= 0) return undefined;

  const intervalsAboveZero = Math.round(top / barStep);
  const intervalsBelowZero = Math.round(-barTicks[0] / barStep);
  const lineMin = Math.min(0, ...lineValues);
  const lineStep = niceStepOf(
    Math.max(
      Math.max(0, ...lineValues) / intervalsAboveZero,
      intervalsBelowZero > 0 ? -lineMin / intervalsBelowZero : 0,
    ),
    lineTickUnit,
  );
  // A line dipping below zero where the bars do not (a negative margin ratio) extends both axes downwards
  // by whole intervals, so the zero gridline stays shared.
  const extraBelow = Math.max(0, Math.ceil(-lineMin / lineStep - 1e-9) - intervalsBelowZero);
  const alignedBarTicks = [
    ...Array.from({ length: extraBelow }, (_, index) => barTicks[0] - (extraBelow - index) * barStep),
    ...barTicks,
  ];

  return { barTicks: alignedBarTicks, lineTicks: alignedBarTicks.map((tick) => Math.round(tick / barStep) * lineStep) };
};

type TickKind = "label" | "dot";

// First bar always labelled, then a label every labelStride bars and dots in between.
// Stride is derived from a target label count so it scales smoothly: a 13-bar range
// stays at every-second (stride 2) instead of stepping to two dots per gap, which
// overlap into smudges on narrow widths.
const xTickKindFor = (index: number, length: number): TickKind => {
  const labelStride = Math.max(2, Math.ceil(length / TARGET_LABEL_COUNT));
  if (index === 0) return "label";
  return index % labelStride === 0 ? "label" : "dot";
};

// The year row reads each bar's year straight from its ISO bucket start (correct for any granularity).
const yearForIndex = (periodStarts: string[], index: number): string | null => {
  const iso = periodStarts[index];
  if (iso === undefined) return null;

  const bucket = new Date(iso);
  return Number.isNaN(bucket.getTime()) ? null : String(bucket.getUTCFullYear());
};

const spansTwoYears = (periodStarts: string[]): boolean => {
  if (periodStarts.length === 0) return false;

  const first = new Date(periodStarts[0]);
  const last = new Date(periodStarts[periodStarts.length - 1]);

  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return false;

  return first.getUTCFullYear() !== last.getUTCFullYear();
};

const yearLabelAt = (periodStarts: string[], index: number): string | null => {
  const currentYear = yearForIndex(periodStarts, index);
  if (currentYear === null) return null;

  if (index === 0) return currentYear;

  const previousYear = yearForIndex(periodStarts, index - 1);
  return previousYear === currentYear ? null : currentYear;
};

const XTick = (props: {
  length: number;
  showYearRow: boolean;
  periodStarts: string[];
  axisLabels: string[];
  barEmphasis?: BarEmphasisPerBar;
  x?: number;
  y?: number;
  index?: number;
}) => {
  const { x, y, index, length, showYearRow, periodStarts, axisLabels, barEmphasis } = props;
  if (x === undefined || y === undefined || index === undefined) return null;

  const kind = xTickKindFor(index, length);
  const yearLabel = showYearRow ? yearLabelAt(periodStarts, index) : null;
  const tickFill = barEmphasis?.[index] === "highlighted" ? LEAD_SERIES_FILL : CHART_AXIS;

  return (
    <g {...dimProps(index)}>
      {kind === "label" && (
        <text
          x={x}
          y={y + 14}
          textAnchor="middle"
          fontFamily="InterVariable, sans-serif"
          fontSize={11}
          fontWeight={CHART_AXIS_FONT_WEIGHT}
          fill={tickFill}
        >
          {axisLabels[index]}
        </text>
      )}
      {kind === "dot" && <circle cx={x} cy={y + 10} r={1.5} fill={tickFill} />}
      {yearLabel && (
        <text
          x={x}
          y={y + 28}
          textAnchor="middle"
          fontFamily="InterVariable, sans-serif"
          fontSize={10}
          fontWeight={CHART_AXIS_FONT_WEIGHT}
          fill={CHART_AXIS}
        >
          {yearLabel}
        </text>
      )}
    </g>
  );
};

const TOOLTIP_CONTENT_STYLE = {
  borderRadius: 12,
  border: `1px solid ${CHART_GRID}`,
  background: "rgba(255,255,255,0.92)",
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  padding: "8px 10px",
  fontFamily: "InterVariable, sans-serif",
  fontSize: 12,
} as const;

const TOOLTIP_LABEL_STYLE = {
  marginBottom: 4,
  fontWeight: 600,
  color: CHART_VALUE,
} as const;

const TOOLTIP_ITEM_STYLE = { padding: 0, fontSize: 12, color: CHART_VALUE } as const;

export interface TrendLineSeries {
  /** Current-period series. */
  leadSeries: YSeries;
  /** Prior-period series. */
  comparisonSeries?: YSeries;
  /** Formats the right axis and the line's tooltip values, in the line's unit. */
  valueFormatter: (value: number, options: { compact: boolean }) => string;
  /** Smallest right-axis step, in the line's unit; ticks are whole multiples of it. Defaults to 1. */
  tickUnit?: number;
}

export interface TrendBarChartProps {
  data: BarChartSchema["data"];
  /**
   * Key in each data row for the x-axis category. Its value must be unique per row: recharts resolves
   * an axis tooltip by matching this key, so a repeated value (e.g. "Q4" in two different years) makes
   * every duplicate bar show the first match. Use a stable id like the period start; {@link axisLabels}
   * carries the human-readable tick text.
   */
  xField: string;
  /** Primary (current-period) series; its field selects the lead bar values. */
  leadSeries: YSeries;
  /**
   * Comparison (prior-period) series. When set and the chart has at most
   * {@link PAIR_MODE_MAX_BARS} bars, a thin marker bar is paired with each lead
   * bar. Omit to render lead bars only.
   */
  comparisonSeries?: YSeries;
  /**
   * ISO start date of each bar, in render order. Drives the whole year row: it shows when these
   * span two calendar years, and each bar's year is read from its own entry.
   */
  periodStarts: string[];
  /** Compact x-tick text per bar, in render order. Separate from the x key so a label may repeat across years. */
  axisLabels: string[];
  /** Per bar, in render order. A muted entry dims its bar, a highlighted entry emphasizes its x tick. */
  barEmphasis?: BarEmphasisPerBar;
  currency: CurrencyCode;
  /** BCP 47 locale for value formatting. Falls back to the format util default. */
  locale?: string;
  /** Row key holding the full tooltip title (e.g. "April"). Falls back to the x label. */
  tooltipLabelField?: string;
  /**
   * Formats axis and tooltip values in the series' unit. `compact` requests a shortened
   * form for the dense y-axis. Defaults to currency, so revenue charts need not set it.
   */
  valueFormatter?: (value: number, options: { compact: boolean }) => string;
  /**
   * A second measure drawn as lines against its own right-hand axis, its fields read from the same
   * data rows.
   */
  lineSeries?: TrendLineSeries;
  /** Index into {@link data} of the focused bar, or null for none: every other column fades. */
  selectedIndex?: number | null;
  /**
   * Reports the tapped bar column, hit-testing the whole band rather than the bar. Passing it
   * replaces the hover tooltip with tap-to-select, so the caller must surface the selected bucket's
   * values itself. The caller owns the selection state and its toggle semantics.
   *
   * Important, there is no keyboard equivalent. Only enable this where pointer input is the only
   * input, otherwise keyboard users lose the tooltip with nothing in its place.
   */
  onSelect?: (index: number) => void;
}

export function TrendBarChart({
  data,
  xField,
  leadSeries,
  comparisonSeries,
  periodStarts,
  axisLabels,
  barEmphasis,
  currency,
  locale,
  tooltipLabelField,
  valueFormatter,
  lineSeries,
  selectedIndex,
  onSelect,
}: TrendBarChartProps) {
  const selectable = onSelect !== undefined;

  // The tap targets sit inside the memoized tree below, so the handler is reached through a ref
  // instead of being a memo dependency. A caller could keep its handler stable only via the setState
  // updater form; the ref removes that requirement, so no call site can remount the bars mid-fade by
  // passing an ordinary inline handler.
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  });
  const selectBand = useCallback((index: number) => onSelectRef.current?.(index), []);

  // Important, the memo excludes the focus index: it only sets CSS custom properties on the wrapper.
  // Re-rendering recharts remounts every bar <g> (its bar subtree is keyed by an animation id derived
  // from prop identity), and a remounted node paints at its final opacity, so the dim fade would snap.
  const chart = useMemo(() => {
    const showYearRow = spansTwoYears(periodStarts);
    const pairMode = comparisonSeries !== undefined && data.length <= PAIR_MODE_MAX_BARS;
    const formatValue =
      valueFormatter ??
      ((value: number, { compact }: { compact: boolean }) =>
        compact ? formatCompact(value) : formatCurrency(value, currency, locale));

    const lines = lineSeries
      ? [
          ...(lineSeries.comparisonSeries
            ? [{ series: lineSeries.comparisonSeries, style: LINE_STYLE.comparison }]
            : []),
          { series: lineSeries.leadSeries, style: LINE_STYLE.lead },
        ]
      : [];
    const lineFields = new Set(lines.map(({ series }) => series.field));
    const barValues = numericValues(data, [leadSeries.field, pairMode ? comparisonSeries?.field : undefined]);
    const ticks = lineSeries
      ? alignedAxisTicks(barValues, numericValues(data, [...lineFields]), lineSeries.tickUnit)
      : undefined;
    const singleBarTicks = niceBarTicks(barValues);
    const barTicks = lineSeries
      ? ticks?.barTicks
      : singleBarTicks[singleBarTicks.length - 1] > 0
        ? singleBarTicks
        : undefined;
    const formatBarTick = (value: number) => keepOnOneLine(formatValue(value, { compact: true }));
    const formatLineTick = (value: number) => keepOnOneLine(lineSeries?.valueFormatter(value, { compact: true }) ?? "");
    const barAxisWidth = barTicks ? axisWidthFor(barTicks.map(formatBarTick)) : AXIS_WIDTH;
    const lineAxisWidth = ticks ? axisWidthFor(ticks.lineTicks.map(formatLineTick)) : AXIS_WIDTH;
    const seriesRank = [leadSeries.field, comparisonSeries?.field, ...[...lineFields].reverse()];
    const formatTooltipValue = (value: number, field: unknown) =>
      lineSeries && lineFields.has(String(field))
        ? lineSeries.valueFormatter(value, { compact: false })
        : formatValue(value, { compact: false });

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 8, right: lineSeries ? 0 : AXIS_LABEL_INSET, left: 0, bottom: showYearRow ? 14 : 0 }}
          barGap={BAR_GAP}
          barCategoryGap="20%"
        >
          <CartesianGrid strokeDasharray="2 3" stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey={xField}
            interval={0}
            tick={
              <XTick
                length={data.length}
                showYearRow={showYearRow}
                periodStarts={periodStarts}
                axisLabels={axisLabels}
                barEmphasis={barEmphasis}
              />
            }
            tickLine={false}
            axisLine={false}
            tickMargin={8}
          />
          <YAxis
            niceTicks="snap125"
            ticks={barTicks}
            domain={barTicks ? [barTicks[0], barTicks[barTicks.length - 1]] : undefined}
            tick={{
              fontFamily: AXIS_LABEL_FONT_FAMILY,
              fontSize: 11,
              fontWeight: CHART_AXIS_FONT_WEIGHT,
              fill: CHART_AXIS,
            }}
            tickFormatter={(v: string | number) => formatBarTick(Number(v))}
            tickLine={false}
            tickSize={0}
            tickMargin={AXIS_LABEL_GAP}
            axisLine={false}
            width={barAxisWidth}
          />
          {lineSeries && (
            <YAxis
              yAxisId={LINE_AXIS_ID}
              orientation="right"
              niceTicks="snap125"
              allowDecimals={(lineSeries.tickUnit ?? 1) < 1}
              ticks={ticks?.lineTicks}
              domain={ticks ? [ticks.lineTicks[0], ticks.lineTicks[ticks.lineTicks.length - 1]] : undefined}
              tick={{
                fontFamily: AXIS_LABEL_FONT_FAMILY,
                fontSize: 11,
                fontWeight: CHART_AXIS_FONT_WEIGHT,
                fill: CHART_AXIS,
              }}
              tickFormatter={(v: string | number) => formatLineTick(Number(v))}
              tickLine={false}
              tickSize={0}
              tickMargin={AXIS_LABEL_GAP}
              axisLine={false}
              width={lineAxisWidth}
            />
          )}
          {!selectable && (
            <Tooltip
              isAnimationActive={false}
              position={{ y: 0 }}
              cursor={{ fill: CHART_GRID }}
              separator=": "
              itemSorter={(item) => seriesRank.indexOf(String(item.dataKey))}
              labelFormatter={(label, payload) => {
                const full = tooltipLabelField ? payload?.[0]?.payload?.[tooltipLabelField] : undefined;
                return typeof full === "string" ? full : label;
              }}
              formatter={(value, _name, item) => formatTooltipValue(Number(value), item.dataKey)}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
            />
          )}
          {pairMode && comparisonSeries && (
            <Bar
              dataKey={comparisonSeries.field}
              name={comparisonSeries.label}
              fill={COMPARISON_SERIES_FILL}
              shape={<PastBarShape leadField={leadSeries.field} />}
              isAnimationActive={false}
            />
          )}
          <Bar
            dataKey={leadSeries.field}
            name={leadSeries.label}
            fill={LEAD_SERIES_FILL}
            shape={
              <LeadBarShape pairMode={pairMode} comparisonField={comparisonSeries?.field} barEmphasis={barEmphasis} />
            }
            isAnimationActive={false}
          />
          {/* Each line sits on its own halo, painted pairwise so the lead line's halo also cuts the
              comparison line where the two cross. */}
          {lines.flatMap(({ series, style }) => [
            <Line
              key={`${series.field}-halo`}
              yAxisId={LINE_AXIS_ID}
              dataKey={series.field}
              type="monotone"
              stroke={CHART_HALO}
              strokeWidth={style.haloWidth}
              strokeLinecap="round"
              dot={false}
              activeDot={false}
              tooltipType="none"
              legendType="none"
              isAnimationActive={false}
            />,
            <Line
              key={series.field}
              yAxisId={LINE_AXIS_ID}
              dataKey={series.field}
              name={series.label}
              type="monotone"
              stroke={style.stroke}
              strokeWidth={style.strokeWidth}
              strokeLinecap="round"
              dot={<LineDot radius={style.dotRadius} fill={style.stroke} />}
              activeDot={false}
              isAnimationActive={false}
            />,
          ])}
          {selectable && <BandTapTargets count={data.length} onSelect={selectBand} />}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }, [
    data,
    xField,
    leadSeries,
    comparisonSeries,
    periodStarts,
    axisLabels,
    barEmphasis,
    currency,
    locale,
    tooltipLabelField,
    valueFormatter,
    lineSeries,
    selectable,
    selectBand,
  ]);

  const columnVars: Record<string, number | string> = {};
  if (selectedIndex != null) {
    data.forEach((_, index) => {
      if (index !== selectedIndex) columnVars[dimVar(index)] = DIMMED_OPACITY;
    });
    columnVars[focusVar(selectedIndex)] = FOCUSED_DOT_GROWTH;
  }

  return <ChartWrap style={columnVars as CSSProperties}>{chart}</ChartWrap>;
}
